import { useCallback, useEffect } from 'react';

import { useQueryClient } from '@tanstack/react-query';

import type { PlanListResponse } from '@rheora/enrollment-contract';

import { fetchPlanDetail, fetchPlans } from './plans.fetchers';
import { PLANS_STALE_MS, seedPricePointsCache, shopQueryKeys } from './plans.queries';

/**
 * Warm a plan's detail before the shopper asks for it.
 *
 * ── WHY ────────────────────────────────────────────────────────────────────
 * `GET /bff/plans/:id` measured 1.45-1.55s cold. Every shopper pays it, in full,
 * between clicking a card and seeing anything — the shelf is already rendered,
 * so the wait is pure dead time on a page that looks finished.
 *
 * Hovering or focusing a card is a reliable signal that it is about to be
 * clicked, and it happens whole seconds earlier. The request is the same
 * request; only its start time moves.
 *
 * ── WHY A HOOK AND NOT A CALL IN THE CARD ──────────────────────────────────
 * Server state goes through TanStack Query, and components render. The card gets
 * a handler to attach to `onMouseEnter`/`onFocus` and stays presentational.
 *
 * ── WHY `prefetchQuery` AND NOT `fetchQuery` ───────────────────────────────
 * `prefetchQuery` resolves to `void` and SWALLOWS errors by design. A prefetch
 * that fails must be invisible: the shopper has not asked for anything yet, and
 * the real `usePlanDetailQuery` on the detail page will fail loudly on its own
 * if the plan is genuinely unreachable. Surfacing a hover as an error state
 * would be a defect introduced by an optimisation.
 *
 * It also respects `staleTime`, so hovering the same card repeatedly — which is
 * what a mouse crossing a shelf actually does — issues at most one request per
 * stale window.
 */
export function usePlanDetailPrefetch(): (planId: string | undefined) => void {
  const queryClient = useQueryClient();

  return useCallback(
    (planId: string | undefined): void => {
      // A card with no id has nothing to warm. Guarding here rather than at each
      // call site keeps the cards free of the check.
      if (planId === undefined || planId.length === 0) return;

      void queryClient.prefetchQuery({
        queryKey: shopQueryKeys.planDetail(planId),
        queryFn: () => fetchPlanDetail(planId),
        staleTime: PLANS_STALE_MS,
      });
    },
    [queryClient],
  );
}

/**
 * Warm the NARROWED shelf the moment a meter resolves.
 *
 * ── WHY THIS EXISTS ────────────────────────────────────────────────────────
 * Narrowing the shelf server-side (`?tdu=`) is a clear win for the meter-first
 * funnel, which goes hero → shelf and now prices 20 tariffs instead of 44. It is
 * a small LOSS for the shopper who browsed the landing page first: that page
 * fetched the whole state under its own cache key, so the narrowed shelf is a
 * second, cold key and costs a list call the shopper did not pay before.
 *
 * This closes that gap. The landing page mounts this hook; the instant the meter
 * resolves — which happens ON the landing page, in the hero, seconds before the
 * shopper clicks "Shop plans" — the narrowed shelf starts warming under exactly
 * the key `MeterPlansPage` will read. Neither entry path regresses.
 *
 * ── WHY AN EFFECT, WHEN THE DETAIL PREFETCH ABOVE IS A CALLBACK ────────────
 * Hovering a card is an EVENT, so that one is a handler. A meter resolving is a
 * STATE TRANSITION, and it can arrive three ways — the ESI ID sub-flow, the ZIP
 * sub-flow, or a funnel rehydrate on a returning tab. Watching the resolved TDU
 * catches all three at one mount point; hanging it off the lookup mutation would
 * catch the first two and silently miss the third.
 *
 * This is not the banned `useEffect`-as-data-loading pattern: nothing is loaded
 * INTO component state, no `LoadState` is tracked, and the component renders
 * identically whether the prefetch runs, succeeds or fails.
 *
 * ── SILENT ON FAILURE ──────────────────────────────────────────────────────
 * `prefetchQuery` swallows errors by design. The shopper has not asked for the
 * shelf yet; if it is genuinely unreachable, `MeterPlansPage`'s own query will
 * fail on its own terms and render its own error state. A hover or a resolve
 * must never be what surfaces an error.
 */
export function usePrefetchShelfForTdu(tdu: string | undefined): void {
  const queryClient = useQueryClient();

  useEffect(() => {
    if (tdu === undefined || tdu.length === 0) return;

    // `staleTime` makes a repeat a no-op, so a re-render, a navigation back to
    // the landing page, or a rehydrate costs nothing.
    void queryClient.prefetchQuery({
      queryKey: shopQueryKeys.plans(tdu),
      queryFn: async (): Promise<PlanListResponse> => {
        const response = await fetchPlans(tdu);
        // Seed the per-card price keys exactly as `usePlansQuery` does, so the
        // shelf's cards are warm too rather than only its list.
        seedPricePointsCache(queryClient, response);
        return response;
      },
      staleTime: PLANS_STALE_MS,
    });
  }, [queryClient, tdu]);
}
